import { Component } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { SplashScreen } from '@ionic-native/splash-screen/ngx';
import { StatusBar } from '@ionic-native/status-bar/ngx';
import { AlertController, Platform } from '@ionic/angular';
import { ConnectionService } from './core/connection/connection.service';
import { LoggerService } from './core/logger/logger.service';
import { RequestQueueService } from './core/services/requestQueue/request-queue.service';
import { LocalStorageService } from './core/storage/local-storage/local-storage.service';
import { SyncTimerService } from './core/sync/sync-timer.service';
import { SyncService } from './core/sync/sync.service';


@Component({
	selector: 'app-root',
	templateUrl: 'app.component.html',
	styleUrls: ['./app.component.scss']
})
export class AppComponent {
	deferredPrompt: any;
	showPwaInstallBanner = false;

	constructor(
		private platform: Platform,
		private splashScreen: SplashScreen,
		private statusBar: StatusBar,
		private connectionService: ConnectionService,
		private syncTimerService: SyncTimerService,
		private swUpdate: SwUpdate,
		private alertController: AlertController,
		private requestQueueService: RequestQueueService,
		private logger: LoggerService,
		private storage: LocalStorageService,
		private syncService: SyncService,
	) {
		this.initializeApp();
	}

	private async initializeApp() {
		await this.platform.ready();
		this.statusBar.styleDefault();
		this.splashScreen.hide();

		this.promptForServiceWorkerUpdates();
		this.addInstallEventListener();
		this.addConnectionReplayListener();

		if (await this.storage.hasNewIDBVersion()) {
			this.syncService.sync();
		}

		this.syncTimerService.periodicSync();
	}


	addToHomeScreen() {
		// Show the prompt
		this.deferredPrompt.prompt();
		// Wait for the user to respond to the prompt
		this.deferredPrompt.userChoice
			.then((choiceResult) => {
				if (choiceResult.outcome === 'accepted') {
					this.logger.logTrace('User accepted the A2HS prompt');
					this.showPwaInstallBanner = false;
				} else {
					this.logger.logTrace('User dismissed the A2HS prompt');
				}
				this.deferredPrompt = null;
			});
	}

	private async promptForServiceWorkerUpdates(): Promise<void> {
		if (this.swUpdate.isEnabled) {
			this.swUpdate.available.subscribe(async () => {
				if (this.syncService.isSyncing$.value) {
					return;
				}
				await this.alertNewVersionAvailable();
			});
		}
	}

	private async alertNewVersionAvailable(): Promise<void> {
		const newVersionAlert = await this.alertController.create({
			header: 'New Version',
			message: 'A new application version has been detected that requires the application to be reloaded.',
			backdropDismiss: false,
			buttons: [
				{
					text: 'Reload',
					handler: () => {
						window.location.reload();
					},
				},
			]
		});

		await newVersionAlert.present();
	}

	private addConnectionReplayListener() {
		this.connectionService.online$.subscribe(
			(isOnline) => {
				if (isOnline) {
					this.requestQueueService.replayFailedRequests();
				}
			}
		);
	}

	private addInstallEventListener() {
		window.addEventListener('beforeinstallprompt', async (e) => {
			// Prevent Chrome 67 and earlier from automatically showing the prompt
			e.preventDefault();
			// Stash the event so it can be triggered later.
			this.deferredPrompt = e;
			this.showPwaInstallBanner = true;
		});
	}
}
